import React, { useState, useEffect, useRef } from "react";
import { useAuth } from '../hooks/useAuth';
import imageCompression from 'browser-image-compression';

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');`;

const STYLES = `
  * { margin:0; padding:0; box-sizing:border-box; }
  :root {
    --bg:#0A0A0F; --surface:#16161F; --surface2:#1E1E2E;
    --coral:#FF5C5C; --orange:#FF8A3D; --cyan:#00E5CC; --purple:#A855F7;
    --gold:#FFD166; --green:#22C55E; --blue:#3B82F6;
    --text:#F0EEF8; --muted:#7B7A8E; --border:rgba(255,255,255,0.08);
    --input-bg:#13131C; --input-border:rgba(255,255,255,0.1);
  }
  body, html, #root { 
    background:var(--bg); 
    color:var(--text); 
    font-family:'DM Sans',sans-serif; 
    height: 100%;
    width: 100%;
    margin: 0;
  }
  .syne { font-family:'Syne',sans-serif; }
  ::-webkit-scrollbar { width:3px; } ::-webkit-scrollbar-thumb { background:var(--surface2); border-radius:2px; }

  @keyframes fadeUp   { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn   { from{opacity:0} to{opacity:1} }
  @keyframes slideL   { from{opacity:0;transform:translateX(30px)} to{opacity:1;transform:translateX(0)} }
  @keyframes slideR   { from{opacity:0;transform:translateX(-30px)} to{opacity:1;transform:translateX(0)} }
  @keyframes float    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
  @keyframes ripple   { 0%{transform:scale(1);opacity:.45} 100%{transform:scale(2.2);opacity:0} }
  @keyframes pulse    { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(.92)} }
  @keyframes shimmer  { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
  @keyframes checkPop { 0%{transform:scale(0) rotate(-10deg)} 70%{transform:scale(1.2)} 100%{transform:scale(1)} }
  @keyframes progressFill { from{width:0%} to{width:var(--target-w)} }
  @keyframes badgeBounce { 0%{transform:translateY(0)} 50%{transform:translateY(-4px)} 100%{transform:translateY(0)} }

  .input-field {
    width:100%; background:var(--input-bg);
    border:1.5px solid var(--input-border);
    border-radius:14px; padding:14px 16px;
    color:var(--text); font-family:'DM Sans',sans-serif;
    font-size:14px; outline:none;
    transition:border-color .25s, box-shadow .25s;
  }
  .input-field:focus {
    border-color:rgba(255,92,92,.5);
    box-shadow:0 0 0 3px rgba(255,92,92,.08);
  }
  .input-field::placeholder { color:var(--muted); }

  .btn-primary {
    background:linear-gradient(135deg,var(--coral),var(--orange));
    border:none; border-radius:14px; color:#fff;
    font-family:'Syne',sans-serif; font-weight:700;
    cursor:pointer; transition:all .25s ease;
    letter-spacing:.02em;
    display:flex;
    align-items:center;
    justify-content:center;
    gap:8px;
  }
  .btn-primary:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 12px 32px rgba(255,92,92,.38); }
  .btn-primary:disabled { opacity:.5; cursor:not-allowed; transform:none; }

  .social-btn {
    background:var(--surface); border:1.5px solid var(--border);
    border-radius:14px; color:var(--text);
    font-family:'DM Sans',sans-serif; font-weight:500;
    cursor:pointer; transition:all .22s ease;
    display:flex; align-items:center; justify-content:center; gap:10px;
  }
  .social-btn:hover { background:var(--surface2); border-color:rgba(255,255,255,.15); transform:translateY(-1px); }

  .interest-chip {
    border-radius:12px; padding:10px 14px; cursor:pointer;
    border:1.5px solid var(--border); background:var(--surface);
    transition:all .22s ease; display:flex; align-items:center; gap:8px;
    font-family:'DM Sans',sans-serif; font-size:13px; font-weight:500;
  }
  .interest-chip.selected {
    background:rgba(255,92,92,.12);
    border-color:rgba(255,92,92,.4);
    color:var(--coral);
  }
  .interest-chip:hover:not(.selected) { background:var(--surface2); border-color:rgba(255,255,255,.15); }
  
  .auth-container {
      width: 100%;
      height: 100vh;
      display: flex;
      justify-content: center;
      align-items: center;
      background: #000;
      background-image: radial-gradient(ellipse at 15% 50%,rgba(255,92,92,.04) 0%,transparent 55%), radial-gradient(ellipse at 85% 50%,rgba(0,229,204,.03) 0%,transparent 55%);
  }

  .auth-card {
      width: 100%;
      height: 100%;
      position: relative;
      overflow: hidden;
      background: var(--bg);
  }

  @media (min-width: 480px) {
      .auth-card {
          width: 400px;
          height: 850px;
          max-height: 95vh;
          border-radius: 40px;
          box-shadow: 0 40px 100px rgba(0,0,0,.92),0 0 0 1px rgba(255,255,255,.09);
      }
  }
`;

const Row = ({ children, style = {} }: { children: React.ReactNode, style?: React.CSSProperties }) => <div style={{ display: 'flex', alignItems: 'center', ...style }}>{children}</div>;
const Col = ({ children, style = {} }: { children: React.ReactNode, style?: React.CSSProperties }) => <div style={{ display: 'flex', flexDirection: 'column', ...style }}>{children}</div>;

// ─── LOGO ─────────────────────────────────────────────────────────────────────
function Logo({ size = 44 }: { size?: number }) {
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <div style={{
        position: 'absolute', inset: 0, borderRadius: size * .28,
        background: 'linear-gradient(135deg,var(--coral),var(--orange))',
        boxShadow: '0 12px 36px rgba(255,92,92,.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'float 3.5s ease-in-out infinite'
      }}>
        <svg width={size * .52} height={size * .52} viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="20" r="10" fill="white" opacity=".95" />
          <path d="M10 38c0-7.732 6.268-14 14-14s14 6.268 14 14" stroke="white" strokeWidth="3" strokeLinecap="round" opacity=".95" />
          <circle cx="36" cy="16" r="5" fill="white" opacity=".6" />
          <path d="M40 28c0-4.418-3.582-8-8-8" stroke="white" strokeWidth="2.5" strokeLinecap="round" opacity=".6" />
        </svg>
      </div>
    </div>
  );
}

// ─── PROGRESS BAR ─────────────────────────────────────────────────────────────
function StepBar({ step, total }: { step: number, total: number }) {
  return (
    <div style={{ padding: '0 24px', marginBottom: 28 }}>
      <Row style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        {Array(total).fill(0).map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 3, borderRadius: 2, marginRight: i < total - 1 ? 6 : 0,
            background: i < step ? 'linear-gradient(90deg,var(--coral),var(--orange))' : i === step ? 'rgba(255,92,92,.3)' : 'var(--surface2)',
            transition: 'background .4s ease', position: 'relative', overflow: 'hidden'
          }}>
            {i === step - 1 && <div style={{
              position: 'absolute', inset: 0, borderRadius: 2,
              background: 'linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.15),rgba(255,255,255,0))',
              backgroundSize: '400px 100%', animation: 'shimmer 1.5s infinite'
            }} />}
          </div>
        ))}
      </Row>
      <Row style={{ justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>Adım {step}/{total}</span>
        <span style={{ fontSize: 11, color: 'var(--coral)', fontWeight: 600, fontFamily: 'Syne' }}>
          {['', 'Hesap Oluştur', 'Profilini Kur', 'İlgi Alanları'][step]}
        </span>
      </Row>
    </div>
  );
}

// ─── SCREEN 0: LANDING ────────────────────────────────────────────────────────
function LandingScreen({ onRegister, onLogin }: { onRegister: () => void, onLogin: () => void }) {
  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: 'var(--bg)', position: 'relative', overflow: 'hidden',
      animation: 'fadeIn .4s ease'
    }}>

      {/* Background blobs */}
      <div style={{
        position: 'absolute', width: 360, height: 360, borderRadius: '50%',
        background: 'radial-gradient(circle,rgba(255,92,92,.13) 0%,transparent 70%)',
        top: '-80px', left: '-60px', filter: 'blur(50px)', pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute', width: 280, height: 280, borderRadius: '50%',
        background: 'radial-gradient(circle,rgba(168,85,247,.1) 0%,transparent 70%)',
        bottom: '20%', right: '-40px', filter: 'blur(50px)', pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute', width: 200, height: 200, borderRadius: '50%',
        background: 'radial-gradient(circle,rgba(0,229,204,.08) 0%,transparent 70%)',
        bottom: '-40px', left: '30%', filter: 'blur(40px)', pointerEvents: 'none'
      }} />

      {/* Top area */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '40px 28px 24px', gap: 0, zIndex: 1
      }}>

        {/* Logo + wordmark */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, marginBottom: 40, animation: 'fadeUp .5s ease .1s both' }}>
          <Logo size={72} />
          <div style={{ textAlign: 'center' }}>
            <div className="syne" style={{
              fontSize: 34, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1,
              background: 'linear-gradient(135deg,#fff 40%,rgba(255,138,61,.75))',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
            }}>
              buddy<span style={{
                background: 'linear-gradient(135deg,var(--coral),var(--orange))',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
              }}>live</span>
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 6, letterSpacing: '.06em' }}>
              Canlı yayın • Sesli oda • Topluluk
            </div>
          </div>
        </div>

        {/* Trust badges */}
        <div style={{ display: 'flex', gap: 20, marginBottom: 40, animation: 'fadeUp .5s ease .25s both' }}>
          {[
            { icon: '🔒', label: 'Güvenli' },
            { icon: '⚡', label: 'Hızlı' },
            { icon: '🌍', label: 'Global' },
          ].map((b, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 14,
                background: 'var(--surface)', border: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
                animation: `badgeBounce 2.5s ease ${i * .3}s infinite`
              }}>
                {b.icon}
              </div>
              <span style={{ fontSize: 10, color: 'var(--muted)', letterSpacing: '.04em' }}>{b.label}</span>
            </div>
          ))}
        </div>

        {/* Social buttons */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10, animation: 'fadeUp .5s ease .35s both' }}>
          <div style={{ textAlign: 'center', marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Hızlı kayıt ile başla</span>
          </div>
          {[
            { icon: <GoogleIcon />, label: 'Google ile Devam Et', bg: '#fff', color: '#1a1a2e', border: 'rgba(255,255,255,.15)' },
            { icon: <AppleIcon />, label: 'Apple ile Devam Et', bg: '#fff', color: '#1a1a2e', border: 'rgba(255,255,255,.15)' },
          ].map((s, i) => (
            <button key={i} onClick={() => console.log('Social login placeholder')} className="social-btn"
              style={{
                width: '100%', padding: '13px 16px', fontSize: 14,
                background: s.bg, color: s.color, border: `1.5px solid ${s.border}`,
                boxShadow: i === 0 || i === 1 ? '0 2px 12px rgba(0,0,0,.3)' : 'none'
              }}>
              <span style={{ width: 22, display: 'flex', justifyContent: 'center' }}>{s.icon}</span>
              <span style={{ fontWeight: 500, fontSize: 14 }}>{s.label}</span>
            </button>
          ))}
        </div>

        {/* Divider */}
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0', animation: 'fadeUp .5s ease .45s both' }}>
          <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>veya</span>
          <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
        </div>

        {/* Email register */}
        <button onClick={onRegister} className="btn-primary"
          style={{ width: '100%', padding: '14px', fontSize: 14, animation: 'fadeUp .5s ease .5s both' }}>
          E-posta ile Kayıt Ol →
        </button>
      </div>

      {/* Login link */}
      <div style={{ padding: '0 28px 36px', textAlign: 'center', flexShrink: 0, animation: 'fadeUp .5s ease .55s both', zIndex: 1 }}>
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>Zaten hesabın var mı? </span>
        <span onClick={onLogin} style={{ fontSize: 13, color: 'var(--coral)', cursor: 'pointer', fontWeight: 600 }}>Giriş Yap</span>
      </div>
    </div>
  );
}

// ─── STEP 1: HESAP ────────────────────────────────────────────────────────────
function Step1Screen({ onNext, onBack, regData, setRegData }: any) {
  const [showPass, setShowPass] = useState(false);
  const [focused, setFocused] = useState('');

  const pass = regData.password;
  const passConf = regData.passwordConfirm;

  const strength = pass.length === 0 ? 0 : pass.length < 6 ? 1 : pass.length < 10 ? 2 : 3;
  const strengthColors = ['', '#FF5C5C', '#FFD166', '#22C55E'];
  const strengthLabels = ['', 'Zayıf', 'Orta', 'Güçlü'];
  const emailValid = regData.email.includes('@') && regData.email.includes('.');
  const canNext = emailValid && pass.length >= 6 && pass === passConf;

  const InputLabel = ({ label, required = true }: { label: string, required?: boolean }) => (
    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', letterSpacing: '.07em', marginBottom: 7 }}>
      {label}{required && <span style={{ color: 'var(--coral)', marginLeft: 2 }}>*</span>}
    </div>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)', animation: 'slideL .38s ease' }}>
      {/* Header */}
      <div style={{ padding: '50px 24px 0', flexShrink: 0 }}>
        <Row style={{ gap: 12, marginBottom: 28 }}>
          <button onClick={onBack} style={{
            width: 36, height: 36, background: 'var(--surface)',
            border: '1px solid var(--border)', borderRadius: 11, cursor: 'pointer', fontSize: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>←</button>
          <div>
            <div className="syne" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em' }}>Hesabını Oluştur</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>E-posta ve şifre ile devam et</div>
          </div>
        </Row>
        <StepBar step={1} total={3} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
        <Col style={{ gap: 18 }}>
          {/* Email */}
          <div>
            <InputLabel label="E-POSTA ADRESİ" />
            <div style={{ position: 'relative' }}>
              <input className="input-field" type="email" placeholder="ornek@email.com"
                value={regData.email} onChange={e => setRegData({ ...regData, email: e.target.value })}
                onFocus={() => setFocused('email')} onBlur={() => setFocused('')} />
              {emailValid && (
                <div style={{
                  position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                  width: 20, height: 20, background: 'var(--green)', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
                  animation: 'checkPop .3s ease'
                }}>✓</div>
              )}
            </div>
          </div>

          {/* Password */}
          <div>
            <InputLabel label="ŞİFRE" />
            <div style={{ position: 'relative' }}>
              <input className="input-field" type={showPass ? 'text' : 'password'}
                placeholder="En az 6 karakter"
                value={pass} onChange={e => setRegData({ ...regData, password: e.target.value })}
                onFocus={() => setFocused('pass')} onBlur={() => setFocused('')}
                style={{ paddingRight: 48 }} />
              <button onClick={() => setShowPass(s => !s)} type="button"
                style={{
                  position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--muted)'
                }}>
                {showPass ? '🙈' : '👁️'}
              </button>
            </div>
            {/* Strength indicator */}
            {pass.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', gap: 4, marginBottom: 5 }}>
                  {[1, 2, 3].map(i => (
                    <div key={i} style={{
                      flex: 1, height: 3, borderRadius: 2,
                      background: i <= strength ? strengthColors[strength] : 'var(--surface2)',
                      transition: 'background .3s ease'
                    }} />
                  ))}
                </div>
                <span style={{ fontSize: 10, color: strengthColors[strength], fontWeight: 600 }}>
                  {strengthLabels[strength]} şifre
                </span>
              </div>
            )}
          </div>

          {/* Confirm password */}
          <div>
            <InputLabel label="ŞİFRE TEKRAR" />
            <div style={{ position: 'relative' }}>
              <input className="input-field" type={showPass ? 'text' : 'password'}
                placeholder="Şifreni tekrar gir"
                value={passConf} onChange={e => setRegData({ ...regData, passwordConfirm: e.target.value })}
                onFocus={() => setFocused('conf')} onBlur={() => setFocused('')}
                style={{
                  paddingRight: 48,
                  borderColor: passConf && pass !== passConf ? 'rgba(255,92,92,.5)' : undefined
                }} />
              {passConf && pass === passConf && (
                <div style={{
                  position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                  width: 20, height: 20, background: 'var(--green)', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
                  animation: 'checkPop .3s ease'
                }}>✓</div>
              )}
            </div>
            {passConf && pass !== passConf && (
              <div style={{ fontSize: 11, color: 'var(--coral)', marginTop: 5 }}>⚠ Şifreler eşleşmiyor</div>
            )}
          </div>

          {/* Trust note */}
          <div style={{
            background: 'rgba(0,229,204,.06)', border: '1px solid rgba(0,229,204,.15)',
            borderRadius: 12, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start'
          }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>🔒</span>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.55)', lineHeight: 1.5 }}>
              Bilgilerin <span style={{ color: 'var(--cyan)' }}>256-bit SSL şifreleme</span> ile korunur.
              Verilerini asla üçüncü taraflarla paylaşmayız.
            </div>
          </div>

          {/* Terms */}
          <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', lineHeight: 1.6 }}>
            Devam ederek <span style={{ color: 'var(--coral)', cursor: 'pointer' }}>Kullanım Şartları</span>'nı ve{' '}
            <span style={{ color: 'var(--coral)', cursor: 'pointer' }}>Gizlilik Politikası</span>'nı kabul etmiş olursun.
          </div>
        </Col>
      </div>

      <div style={{ padding: '16px 24px 36px', flexShrink: 0 }}>
        <button className="btn-primary" onClick={onNext} disabled={!canNext}
          style={{ width: '100%', padding: '15px', fontSize: 15 }}>
          Devam Et →
        </button>
      </div>
    </div>
  );
}

// ─── STEP 2: PROFİL ───────────────────────────────────────────────────────────
function Step2Screen({ onNext, onBack, regData, setRegData }: any) {
  const [usernameStatus, setUsernameStatus] = useState('idle'); // idle | checking | available | taken
  const timerRef = useRef<any>(null);

  const checkUsername = (val: string) => {
    setRegData({ ...regData, username: val });
    if (val.length < 3) {
      setUsernameStatus('idle');
      return;
    }
    setUsernameStatus('checking');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      // Fake check for now, consider an API call here if needed
      setUsernameStatus(['ahmet', 'zeynep', 'admin', 'root', 'buddy'].includes(val.toLowerCase()) ? 'taken' : 'available');
    }, 900);
  };

  const avatarColors = ['#FF5C5C', '#A855F7', '#00E5CC', '#FF8A3D', '#22C55E', '#3B82F6'];
  const [avatarColor, setAvatarColor] = useState(avatarColors[0]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 20 * 1024 * 1024) {
        alert('Dosya boyutu çok büyük (Max 20MB)');
        return;
      }
      try {
        const options = { maxSizeMB: 0.5, maxWidthOrHeight: 1080, useWebWorker: true };
        const compressedFile = await imageCompression(file, options);
        
        const reader = new FileReader();
        reader.onload = (ev) => {
          setRegData({ ...regData, profileImage: ev.target?.result as string });
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('Fotoğraf sıkıştırma hatası:', error);
        alert('Fotoğraf yüklenemedi, lütfen tekrar deneyin.');
      }
    }
  };

  const canNext = regData.name.trim().length >= 2 && regData.username.trim().length >= 3 && usernameStatus === 'available';

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)', animation: 'slideL .38s ease' }}>
      <div style={{ padding: '50px 24px 0', flexShrink: 0 }}>
        <Row style={{ gap: 12, marginBottom: 28 }}>
          <button onClick={onBack} style={{
            width: 36, height: 36, background: 'var(--surface)',
            border: '1px solid var(--border)', borderRadius: 11, cursor: 'pointer', fontSize: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>←</button>
          <div>
            <div className="syne" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em' }}>Profilini Kur</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Seni tanıyalım</div>
          </div>
        </Row>
        <StepBar step={2} total={3} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
        <Col style={{ gap: 20 }}>

          {/* Avatar picker */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  width: 88, height: 88, borderRadius: 26,
                  background: regData.profileImage ? `url(${regData.profileImage}) center/cover` : `linear-gradient(135deg,${avatarColor},${avatarColor}88)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 42,
                  border: `3px solid ${avatarColor}66`,
                  boxShadow: `0 12px 36px ${avatarColor}33`,
                  transition: 'all .3s ease'
                }}
              >
                {!regData.profileImage && (regData.name ? regData.name[0].toUpperCase() : '?')}
              </div>
              <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={handleAvatarUpload} />
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  position: 'absolute', bottom: -2, right: -2, width: 26, height: 26,
                  background: 'var(--surface2)', borderRadius: 9, border: '2px solid var(--bg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, cursor: 'pointer'
                }}
              >
                📷
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {avatarColors.map(c => (
                <div key={c} onClick={() => setAvatarColor(c)} style={{
                  width: 24, height: 24, borderRadius: '50%', background: c, cursor: 'pointer',
                  border: avatarColor === c ? `3px solid #fff` : '3px solid transparent',
                  transition: 'all .2s ease', transform: avatarColor === c ? 'scale(1.15)' : 'scale(1)'
                }} />
              ))}
            </div>
          </div>

          {/* Full name */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', letterSpacing: '.07em', marginBottom: 7 }}>
              AD SOYAD<span style={{ color: 'var(--coral)', marginLeft: 2 }}>*</span>
            </div>
            <input className="input-field" placeholder="Ahmet Yıldız"
              value={regData.name} onChange={e => setRegData({ ...regData, name: e.target.value })} />
          </div>

          {/* Username */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', letterSpacing: '.07em', marginBottom: 7 }}>
              KULLANICI ADI<span style={{ color: 'var(--coral)', marginLeft: 2 }}>*</span>
            </div>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--muted)', fontSize: 14
              }}>@</span>
              <input className="input-field" placeholder="kullaniciadi"
                value={regData.username} onChange={e => checkUsername(e.target.value)}
                style={{
                  paddingLeft: 30, paddingRight: 44,
                  borderColor: usernameStatus === 'taken' ? 'rgba(255,92,92,.5)' :
                    usernameStatus === 'available' ? 'rgba(34,197,94,.4)' : undefined
                }} />
              <div style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', fontSize: 15 }}>
                {usernameStatus === 'checking' && <span style={{ color: 'var(--muted)', animation: 'pulse 1s infinite', display: 'block' }}>⏳</span>}
                {usernameStatus === 'available' && <div style={{ width: 20, height: 20, background: 'var(--green)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, animation: 'checkPop .3s ease' }}>✓</div>}
                {usernameStatus === 'taken' && <span>❌</span>}
              </div>
            </div>
            {usernameStatus === 'taken' && (
              <div style={{ fontSize: 11, color: 'var(--coral)', marginTop: 5 }}>⚠ Bu kullanıcı adı alınmış</div>
            )}
            {usernameStatus === 'available' && (
              <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 5 }}>✓ Kullanıcı adı uygun!</div>
            )}
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
              Harfler, rakamlar ve alt çizgi kullanabilirsin. Sonradan değiştirilebilir.
            </div>
          </div>

          {/* Profile tips */}
          <div style={{
            background: 'rgba(168,85,247,.07)', border: '1px solid rgba(168,85,247,.15)',
            borderRadius: 14, padding: '14px', marginTop: '10px'
          }}>
            <div className="syne" style={{ fontSize: 12, fontWeight: 700, color: '#A855F7', marginBottom: 10 }}>💡 İpuçları</div>
            <Col style={{ gap: 7 }}>
              {[
                'Gerçek adın yerine bir takma ad kullanabilirsin',
                'Profil fotoğrafı yüklemek takipçi sayını artırır',
                'Kullanıcı adın buddy live linkin olacak',
              ].map((tip, i) => (
                <Row key={i} style={{ gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--purple)', marginTop: 6, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,.55)', lineHeight: 1.5 }}>{tip}</span>
                </Row>
              ))}
            </Col>
          </div>
        </Col>
      </div>

      <div style={{ padding: '16px 24px 36px', flexShrink: 0 }}>
        <button className="btn-primary" onClick={onNext} disabled={!canNext}
          style={{ width: '100%', padding: '15px', fontSize: 15 }}>
          Devam Et →
        </button>
      </div>
    </div>
  );
}

// ─── STEP 3: İLGİ ALANLARI ────────────────────────────────────────────────────
const INTERESTS = [
  { id: 'gaming', icon: '🎮', label: 'Gaming', color: '#A855F7' },
  { id: 'music', icon: '🎵', label: 'Müzik', color: '#00E5CC' },
  { id: 'sports', icon: '⚽', label: 'Spor', color: '#22C55E' },
  { id: 'art', icon: '🎨', label: 'Sanat', color: '#F59E0B' },
  { id: 'tech', icon: '💻', label: 'Teknoloji', color: '#3B82F6' },
  { id: 'cooking', icon: '🍳', label: 'Yemek', color: '#FF8A3D' },
  { id: 'travel', icon: '✈️', label: 'Seyahat', color: '#06B6D4' },
  { id: 'fitness', icon: '💪', label: 'Fitness', color: '#EF4444' },
  { id: 'books', icon: '📚', label: 'Kitap', color: '#8B5CF6' },
  { id: 'cinema', icon: '🎬', label: 'Sinema', color: '#EC4899' },
  { id: 'animals', icon: '🐾', label: 'Hayvanlar', color: '#84CC16' },
  { id: 'comedy', icon: '😂', label: 'Komedi', color: '#F59E0B' },
];

function Step3Screen({ onFinish, onBack, loading, error }: any) {
  const [selected, setSelected] = useState<string[]>([]);
  const toggle = (id: string) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const canFinish = selected.length >= 3;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)', animation: 'slideL .38s ease' }}>
      <div style={{ padding: '50px 24px 0', flexShrink: 0 }}>
        <Row style={{ gap: 12, marginBottom: 24 }}>
          <button onClick={onBack} disabled={loading} style={{
            width: 36, height: 36, background: 'var(--surface)',
            border: '1px solid var(--border)', borderRadius: 11, cursor: 'pointer', fontSize: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>←</button>
          <div>
            <div className="syne" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em' }}>İlgi Alanların</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>En az 3 seçmen yeterli</div>
          </div>
        </Row>
        <StepBar step={3} total={3} />

        {/* Selection count */}
        <div style={{ marginBottom: 16 }}>
          <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Seçilen: <span style={{ color: 'var(--coral)', fontWeight: 700 }}>{selected.length}</span> / 12</span>
            {selected.length >= 3 && <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>✓ Devam edebilirsin</span>}
          </Row>
          <div style={{ height: 3, background: 'var(--surface2)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 2,
              background: 'linear-gradient(90deg,var(--coral),var(--orange))',
              width: `${(selected.length / 12) * 100}%`, transition: 'width .35s ease'
            }} />
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
        {/* Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9, paddingBottom: 8 }}>
          {INTERESTS.map(interest => {
            const isSelected = selected.includes(interest.id);
            return (
              <div key={interest.id} onClick={() => toggle(interest.id)}
                className={`interest-chip ${isSelected ? 'selected' : ''}`}
                style={{
                  background: isSelected ? `${interest.color}14` : 'var(--surface)',
                  borderColor: isSelected ? `${interest.color}55` : 'var(--border)',
                  transform: isSelected ? 'scale(1.01)' : 'scale(1)',
                  boxShadow: isSelected ? `0 6px 20px ${interest.color}18` : 'none',
                }}>
                <span style={{ fontSize: 22 }}>{interest.icon}</span>
                <div style={{ flex: 1 }}>
                  <div className="syne" style={{
                    fontSize: 12, fontWeight: 700,
                    color: isSelected ? interest.color : 'var(--text)'
                  }}>
                    {interest.label}
                  </div>
                </div>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                  background: isSelected ? interest.color : 'var(--surface2)',
                  border: `1.5px solid ${isSelected ? interest.color : 'var(--border)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, color: '#fff', transition: 'all .22s ease',
                  animation: isSelected ? 'checkPop .25s ease' : 'none'
                }}>
                  {isSelected ? '✓' : ''}
                </div>
              </div>
            );
          })}
        </div>

        {/* Personalisation note */}
        <div style={{
          background: 'rgba(255,209,102,.06)', border: '1px solid rgba(255,209,102,.15)',
          borderRadius: 12, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8, marginTop: 10
        }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>✨</span>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', lineHeight: 1.5 }}>
            Seçimlerin <span style={{ color: 'var(--gold)' }}>ana sayfan</span>ı ve <span style={{ color: 'var(--gold)' }}>önerilen yayıncılar</span>ı kişiselleştirmek için kullanılır.
          </div>
        </div>

        {error && (
          <div style={{ padding: '10px', background: 'rgba(255,92,92,0.1)', color: 'var(--coral)', borderRadius: '12px', fontSize: 12, textAlign: 'center', marginTop: 10 }}>
            {error}
          </div>
        )}
      </div>

      <div style={{ padding: '14px 24px 36px', flexShrink: 0 }}>
        <button className="btn-primary" onClick={onFinish} disabled={!canFinish || loading}
          style={{
            width: '100%', padding: '15px', fontSize: 15,
            opacity: canFinish ? 1 : .5
          }}>
          {loading ? (
            <>
              <div style={{
                width: 16, height: 16, borderRadius: '50%',
                border: '2px solid rgba(255,255,255,.3)',
                borderTopColor: '#fff', animation: 'spin 0.8s linear infinite'
              }} />
              İşleniyor...
            </>
          ) : (
            canFinish ? 'Hesabı Oluştur 🎉' : 'En az 3 kategori seç'
          )}
        </button>
      </div>
    </div>
  );
}

// ─── SUCCESS SCREEN ───────────────────────────────────────────────────────────
function SuccessScreen({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3200);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(ellipse at center,#0f0a1a 0%,var(--bg) 70%)',
      animation: 'fadeIn .4s ease', padding: '0 32px', gap: 0
    }}>
      <div style={{
        position: 'absolute', width: 300, height: 300, borderRadius: '50%',
        background: 'radial-gradient(circle,rgba(34,197,94,.15) 0%,transparent 70%)',
        filter: 'blur(40px)', pointerEvents: 'none'
      }} />

      {/* Check mark */}
      <div style={{
        width: 100, height: 100, borderRadius: '50%',
        background: 'linear-gradient(135deg,var(--green),#16A34A)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 46,
        boxShadow: '0 20px 60px rgba(34,197,94,.35)',
        marginBottom: 28,
        animation: 'checkPop .5s cubic-bezier(.34,1.56,.64,1) .1s both'
      }}>
        ✓
      </div>

      <div className="syne" style={{
        fontSize: 28, fontWeight: 800, marginBottom: 8,
        letterSpacing: '-.02em', textAlign: 'center',
        animation: 'fadeUp .5s ease .3s both'
      }}>
        Hoş Geldin! 🎉
      </div>
      <div style={{
        color: 'var(--muted)', fontSize: 14, textAlign: 'center', lineHeight: 1.6,
        maxWidth: 260, animation: 'fadeUp .5s ease .4s both'
      }}>
        Hesabın başarıyla oluşturuldu. Buddy Live'a katıldın!
      </div>

      {/* Confetti dots */}
      {['var(--coral)', 'var(--cyan)', 'var(--gold)', 'var(--purple)', 'var(--green)'].map((c, i) => (
        <div key={i} style={{
          position: 'absolute',
          width: 8, height: 8, borderRadius: '50%', background: c,
          top: `${25 + Math.random() * 50}%`,
          left: `${10 + i * 18}%`,
          animation: `float ${2 + i * .3}s ease-in-out ${i * .2}s infinite`
        }} />
      ))}

      <div style={{ marginTop: 32, animation: 'fadeUp .5s ease .55s both' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: 7, height: 7, borderRadius: '50%',
              background: i === 0 ? 'var(--coral)' : 'var(--surface2)',
              animation: `pulse 1.2s ease-in-out ${i * .2}s infinite`
            }} />
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10, textAlign: 'center' }}>
          Ana sayfaya yönlendiriliyorsun...
        </div>
      </div>
    </div>
  );
}

// ─── LOGIN SCREEN ─────────────────────────────────────────────────────────────
function LoginScreen({ onBack, onLoginSuccess, onShowPassword }: any) {
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      await login(email, pass);
      // Login successful
      onLoginSuccess();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Giriş yapılamadı.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: 'var(--bg)', animation: 'slideR .38s ease'
    }}>
      <div style={{
        position: 'absolute', width: 320, height: 320, borderRadius: '50%',
        background: 'radial-gradient(circle,rgba(0,229,204,.1) 0%,transparent 70%)',
        top: '-60px', right: '-60px', filter: 'blur(50px)', pointerEvents: 'none'
      }} />

      <div style={{ padding: '50px 24px 0', flexShrink: 0 }}>
        <Row style={{ gap: 12, marginBottom: 32 }}>
          <button onClick={onBack} disabled={loading} style={{
            width: 36, height: 36, background: 'var(--surface)',
            border: '1px solid var(--border)', borderRadius: 11, cursor: 'pointer', fontSize: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>←</button>
          <div>
            <div className="syne" style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-.02em' }}>Tekrar Hoş Geldin</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Hesabına giriş yap</div>
          </div>
        </Row>

        {/* Social login small */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          {[{ icon: <GoogleIcon size={18} />, label: 'Google' }, { icon: <AppleIcon size={18} />, label: 'Apple' }].map((s, i) => (
            <button key={i} className="social-btn"
              style={{
                flex: 1, padding: '11px', fontSize: 13, gap: 8,
                background: i === 0 ? 'rgba(255,255,255,.95)' : 'rgba(255,255,255,.95)',
                color: '#1a1a2e', border: 'none'
              }}>
              {s.icon}<span style={{ fontWeight: 500 }}>{s.label}</span>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>e-posta ile</span>
          <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
        <Col style={{ gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', letterSpacing: '.07em', marginBottom: 7 }}>E-POSTA</div>
            <input className="input-field" type="email" placeholder="ornek@email.com"
              value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div>
            <Row style={{ justifyContent: 'space-between', marginBottom: 7 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', letterSpacing: '.07em' }}>ŞİFRE</div>
              <span onClick={() => { if (onShowPassword) onShowPassword({ email, username: '' }); }} style={{ fontSize: 11, color: 'var(--coral)', cursor: 'pointer', fontWeight: 600 }}>Şifremi Unuttum</span>
            </Row>
            <div style={{ position: 'relative' }}>
              <input className="input-field" type={showPass ? 'text' : 'password'}
                placeholder="Şifreni gir"
                value={pass} onChange={e => setPass(e.target.value)}
                style={{ paddingRight: 48 }} />
              <button onClick={() => setShowPass(s => !s)} type="button"
                style={{
                  position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--muted)'
                }}>
                {showPass ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {error && (
            <div style={{ padding: '10px', background: 'rgba(255,92,92,0.1)', color: 'var(--coral)', borderRadius: '12px', fontSize: 12, textAlign: 'center' }}>
              {error}
            </div>
          )}

          {/* Remember me */}
          <Row style={{ gap: 10 }}>
            <div style={{
              width: 18, height: 18, borderRadius: 5,
              background: 'rgba(255,92,92,.15)', border: '1.5px solid rgba(255,92,92,.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0
            }}>
              <div style={{ width: 8, height: 8, background: 'var(--coral)', borderRadius: 2 }} />
            </div>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Beni hatırla</span>
          </Row>
        </Col>
      </div>

      <div style={{ padding: '16px 24px 36px', flexShrink: 0 }}>
        <button className="btn-primary" onClick={handleLogin}
          disabled={!email || !pass || loading}
          style={{ width: '100%', padding: '15px', fontSize: 15 }}>
          {loading ? (
            <>
              <div style={{
                width: 16, height: 16, borderRadius: '50%',
                border: '2px solid rgba(255,255,255,.3)',
                borderTopColor: '#fff', animation: 'spin 0.8s linear infinite'
              }} />
              Giriş yapılıyor...
            </>
          ) : 'Giriş Yap →'}
        </button>
        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Hesabın yok mu? </span>
          <span onClick={onBack} style={{ fontSize: 12, color: 'var(--coral)', cursor: 'pointer', fontWeight: 600 }}>Kayıt Ol</span>
        </div>
      </div>
    </div>
  );
}

// ─── SVG ICONS ────────────────────────────────────────────────────────────────
function GoogleIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}
function AppleIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#1a1a2e">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

// ─── APP ROOT (AUTH PAGE) ─────────────────────────────────────────────────────
interface AuthPageProps {
  onShowPassword?: (data: { email: string; username: string }) => void;
}

export default function AuthPage({ onShowPassword }: AuthPageProps) {
  const [screen, setScreen] = useState('landing'); // landing | step1 | step2 | step3 | success | login

  // Registration State
  const [regData, setRegData] = useState<{
    email: string;
    password: string;
    passwordConfirm: string;
    name: string;
    username: string;
    profileImage: string;
  }>({
    email: '', password: '', passwordConfirm: '', name: '', username: '', profileImage: ''
  });

  // Handle hardware back button
  useEffect(() => {
    const handleBack = () => {
      if (screen !== 'landing') {
        if (screen === 'login') setScreen('landing');
        else if (screen === 'step1') setScreen('landing');
        else if (screen === 'step2') setScreen('step1');
        else if (screen === 'step3') setScreen('step2');
      } else {
        import('@capacitor/app').then(({ App }) => App.exitApp());
      }
    };
    window.addEventListener('appBackButton', handleBack);
    return () => window.removeEventListener('appBackButton', handleBack);
  }, [screen]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { register } = useAuth();

  const handleFinalRegister = async () => {
    setLoading(true);
    setError('');
    try {
      // 1) Register user
      await register(regData.username, regData.email, regData.password);

      // 2) Upload profile image if selected
      if (regData.profileImage) {
        try {
          const { userAPI } = await import('../services/api');
          const response = await fetch(regData.profileImage);
          const blob = await response.blob();
          const file = new File([blob], 'profile.jpg', { type: blob.type || 'image/jpeg' });
          await userAPI.uploadProfileImage(file);
        } catch (imgErr) {
          console.error("Profile image upload failed:", imgErr);
        }
      }

      setScreen('success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Kayıt olurken bir hata oluştu.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    window.location.reload(); // Reload to navigate after token is set
  };

  const SCREENS: any = {
    landing: <LandingScreen onRegister={() => setScreen('step1')} onLogin={() => setScreen('login')} />,
    step1: <Step1Screen onNext={() => setScreen('step2')} onBack={() => setScreen('landing')} regData={regData} setRegData={setRegData} />,
    step2: <Step2Screen onNext={() => setScreen('step3')} onBack={() => setScreen('step1')} regData={regData} setRegData={setRegData} />,
    step3: <Step3Screen onFinish={handleFinalRegister} onBack={() => setScreen('step2')} loading={loading} error={error} />,
    success: <SuccessScreen onDone={handleDone} />,
    login: <LoginScreen onBack={() => setScreen('landing')} onLoginSuccess={handleDone} onShowPassword={onShowPassword} />,
  };

  return (
    <>
      <style>{FONTS + STYLES}
        {`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}
      </style>

      <div className="auth-container">
        {/* Main Card (Responsive) */}
        <div className="auth-card">
          {/* Status bar (Only visible conceptually if we want it, maybe remove for web) */}
          {/* <div style={{position:'absolute',top:0,left:0,right:0,height:44,
            display:'flex',alignItems:'center',justifyContent:'space-between',
            padding:'6px 22px 0',zIndex:99,pointerEvents:'none'}}>
            ...
          </div> */}

          <div style={{ position: 'absolute', inset: 0 }}>{SCREENS[screen]}</div>
        </div>
      </div>
    </>
  );
}
